import React, { useState, useEffect } from "react";
import { Box, Fade, Stack, Typography, useMediaQuery } from "@mui/material";
import { Parallax } from "react-scroll-parallax";
import { Trans, useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import theme from "../../../theme";
import MenuCollapse from "../../../components/ModalNavigation/MenuCollapse";
import LanguageSwitcher from "../../../components/LanguageSwitcher/LanguageSwitcher";
import logoImg from "../../../residenzaStoricaAssets/1811white.png";
import backgroundImg1 from "../../../assets/resFinal/landing1.JPG";
import backgroundImg2 from "../../../assets/resFinal/dipinto.webp";
import napoliImage from "../../../assets/Landing/napoli-castel-dell-ovo.webp";
import reslogo from "../../../assets/1811Mobile/reslogo.jpg";

// Preload images for smoother experience
const preloadImages = (srcArray: string[]) => {
  srcArray.forEach((src) => {
    const img = new Image();
    img.src = src;
  });
};

function CustomMobileLanding({ isRooms }: { isRooms?: boolean }) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isLandscape = useMediaQuery("(orientation: landscape)");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageLoaded, setIsImageLoaded] = useState(false); // Stato per monitorare il caricamento dell'immagine
  const location = useLocation();
  const isRoomPage = location.pathname.startsWith("/rooms/");
  const isHome = location.pathname === "/";
  const { t } = useTranslation();

  const images = [backgroundImg1, backgroundImg2]; // Image carousel

  // Precarica le immagini
  useEffect(() => {
    preloadImages(images);
  }, [images]);

  // Aggiungi un event listener per sapere quando l'immagine è stata caricata
  const handleImageLoad = () => {
    setIsImageLoaded(true); // Quando l'immagine è caricata, cambia lo stato
  };

  return (
    <Box
      position="relative"
      width="100vw"
      height={isRooms ? undefined : "100vh"}
    >
      {/* Language Switcher */}
      <Box
        position="absolute"
        top="0"
        right="20px"
        zIndex={9999}
        display={isRoomPage ? "none" : "block"}
      >
        <LanguageSwitcher />
      </Box>

      {/* Top Logo Section */}
      <Box
        sx={{
          width: "100vw",
          height: "15vw",
          display: isRoomPage ? "none" : "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: isHome ? undefined : "#33414C",
          backgroundImage: isHome ? `url(${napoliImage})` : undefined,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        }}
      >
        <Fade in={true} timeout={2000}>
          <img
            src={logoImg}
            alt="Logo"
            style={{
              display: isHome ? "none" : "flex",
              width: isLandscape ? "40%" : isMobile ? "15vw" : "150vw",
            }}
          />
        </Fade>
      </Box>

      <MenuCollapse />
      {/* Background Image Section */}
      <Box
        sx={{
          width: "100vw",
          height: isMobile ? "48vh" : "50vh",
          display: isRooms ? "none" : "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundImage: `url(${images[currentImageIndex]})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
          transition: "background-image 1s ease-in-out",
        }}
      >
        <img
          src={images[currentImageIndex]} // Mostra l'immagine
          alt="background"
          style={{ display: "none" }} // Nascondi l'immagine originale ma caricala
          onLoad={handleImageLoad} // Attiva lo stato di caricamento quando l'immagine è pronta
        />
        <Box
          position="absolute"
          top="0"
          left="0"
          width="100%"
          height="100%"
          zIndex={1}
          bgcolor="rgba(0, 0, 0, 0.4)"
          sx={{ transition: "background-color 0.3s" }}
        />
        {isImageLoaded && ( // Mostra il contenuto solo quando l'immagine è caricata
          <Fade in={true} timeout={2000}>
            <h1 style={{ color: "white", textAlign: "center", zIndex: 4 }}>
              <img
                src={logoImg}
                alt="1811 Residenza Storica"
                style={{
                  zIndex: 4,
                  width: isLandscape ? "40%" : isMobile ? "70vw" : "150vw",
                }}
              />
            </h1>
          </Fade>
        )}
      </Box>
      {/* Parallax Section */}
      <Parallax
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-end",
        }}
        translateY={["20px", "100px"]}
        easing="easeInQuad"
      >
        {/* Content Section */}
        <Stack
          spacing={2}
          sx={{
            width: "100%",
            height: "25%",
            display: isRooms ? "none" : "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Stack
            sx={{
              zIndex: 1000,
              width: "90%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <h1
              style={{
                textAlign: "center",
                margin: 0,
                fontSize: isMobile ? "1.5rem" : "2rem",
              }}
            >
              <img src={reslogo} width="85%" alt="Residence Logo" />
            </h1>
          </Stack>
          <Typography
            fontSize={isMobile ? "14px" : "24px"}
            textAlign="center"
            fontStyle="italic"
            letterSpacing="0.6px"
            lineHeight={1.5}
            sx={{
              maxWidth: isMobile ? "275px" : "700px",
              paddingTop: isMobile ? 0 : 3,
              fontFamily: "degular",
              fontWeight: "400",
            }}
          >
            <Trans
              i18nKey="description"
              components={{
                bold: <b />,
                italic: <i />,
              }}
            />{" "}
          </Typography>
          <KeyboardArrowDownIcon
            sx={{
              fontSize: "5vw",
              mt: isMobile ? 0 : 2,
              color: theme.palette.secondary.main,
            }}
          />
        </Stack>
      </Parallax>
    </Box>
  );
}

export default CustomMobileLanding;
